import {
  GridCellProps,
  GridColumn as Column,
} from '@progress/kendo-react-grid';
import {
  DateCell,
  DateCellContent,
  DateLinkCell,
  FilterBarMainGrid,
  LinkCell,
  PureCell,
} from 'Components/Grid';
import {
  FormProvider,
  GridProvider,
  useEnums,
  useGlobalContext,
  useGridState,
} from 'Context';
import {
  ColumnSelectorProvider,
  useColumnSelectorState,
} from 'Context/ColumnSelectorProvider';
import {
  defaultGridProps,
  FilterBarFilterType,
  FilterConfig,
  IFilterConfig,
  Referral,
  StaticFilterConfig,
} from 'Models';
import {
  DefaultColumnsSelection,
  DefaultColumnsSelectionClosedOpenReferralsDoctor,
} from 'Models/ColumnSelection';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { dayjs } from 'Utilities/DateTimeUtc';
import {
  AddEditSubRoute,
  ClaimantsSubRoute,
  ClaimsSubRoute,
  ReferralSubRoute,
  ROUTES,
} from 'Navigation';
import React, { Fragment, useEffect, useState } from 'react';
import {
  buildEndpoint,
  buildRoute,
  DEFAULT_DATETIME24H_FORMAT,
  EMPTY_GUID,
  useDataFetcher,
  useGridSearch,
} from 'Utilities';
import { useHistory } from 'react-router-dom';
import NewReferralButton from 'v3/Components/Shared/NewReferralButton';
import ClientSelector from 'v3/Components/Shared/ClientSelector';
import { DebouncedTextFilterCell } from 'Components/Grid/FilterCells/DebouncedTextFilterCell';
import { DropdownFilterCell } from 'Components/Grid/FilterCells/DropdownFilterCell';
import { DropdownDbOption } from 'v3/Components/Form/Dropdown';
import { StatusPill } from 'v3/Components/Shared/StatusPill';
import { dueDateTimeCell } from 'Components/Grid/DisplayCells/CustomCells';
import { ColumnMenu } from 'Components/Grid/TableHeader/ColumnMenu';
import { DateRangePickerFilterCell } from 'Components/Grid/FilterCells/DateRangePickerFilterCell';

class OpenReferralsSearch {
  searchKey?: string;
  id?: string;
  client?: string;
  benefitState?: string;
  appointmentDate?: Date;
}

enum Status {
  SUBMITTED = 'Submitted',
  PENDING = 'Pending',
  CONVERTED = 'Converted',
}
export enum OpenReferralsGridIds {
  DetailedSearch = 'detailedSearch',
}

export const OpenReferrals: React.FC = () => {
  const userdata = useGlobalContext()[0]?.currentUser;
  const style = {
    marginTop: '0.5em',
    display: 'block',
  };
  return (
    <ColumnSelectorProvider
      columns={
        userdata?.isDoctor || userdata?.isReviewer
          ? DefaultColumnsSelectionClosedOpenReferralsDoctor
          : DefaultColumnsSelection
      }
      columnSelectorName="openReferralsColumns"
    >
      <GridProvider entityName="Referral">
        <div className="main-grid-header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h1>Open Referrals</h1>
            <div className="grids-header-client-selector">
              <ClientSelector containerClassName="client-selector--large" />
            </div>
          </div>

          <NewReferralButton />
        </div>
        <FilterBarDetailedSearchGrid />
      </GridProvider>
    </ColumnSelectorProvider>
  );
};

export const DueTodayHeader: React.FC = () => {
  const gridState = useGridState();
  let data = gridState.data as Array<Referral>;

  let dueToday = data?.filter((item) =>
    dayjs
      .utc(item.dueDate)
      .local()
      .startOf('day')
      .isSame(dayjs().startOf('day'), 'day')
  ).length;

  return <h6>{dueToday} Referral(s) Due Today</h6>;
};

export const FilterBarDetailedSearchGrid: React.FC = () => {
  const OpenReferralSearch = new OpenReferralsSearch();

  return (
    <FormProvider formModel={OpenReferralSearch}>
      <OpenReferralsGrid />
    </FormProvider>
  );
};

type FilterModel = {
  services?: DropdownDbOption[];
  submittedBy?: DropdownDbOption[];
  subServices?: DropdownDbOption[];
};

const OpenReferralsGrid: React.FC = () => {
  const { state, lineOfBusiness, appointmentStatus, caseStatus } = useEnums();
  const history = useHistory();
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const isClient = userdata?.isClient ?? false;
  const columnSelectorState = useColumnSelectorState();
  const showDateFilters = userdata?.isInternal || userdata?.isAdmin;
  const [filtersModel, filtersStatus] = useDataFetcher<FilterModel>(
    `${ENDPOINTS.opencases.base}/GetFiltersModel`,
    {},
    (d) => d.data,
    undefined,
    true,
    '3.0'
  );

  const [exportEndpoint, setExportEndpoint] = useState(
    buildEndpoint(
      ENDPOINTS.casemanagement.endpointString('ExportToCsv') +
      `${getSelectedColumnsQuery(columnSelectorState)}`
    )
  );

  // add to the appointmentStatus enum a new value for 'Submitted' and empty
  const referralStatus = {
    '---': { value: '', displayName: '---' },
    Submitted: { value: 'Submitted', displayName: 'Submitted' },
    ...appointmentStatus,
  };

  useEffect(() => {
    setExportEndpoint(
      buildEndpoint(
        ENDPOINTS.casemanagement.endpointString('ExportToCsv') +
        `${getSelectedColumnsQuery(columnSelectorState)}`
      )
    );
  }, [columnSelectorState]);

  let searchBarHint: string =
    'Stradix Number, Claim Number, Claimant, Referral Number, Doctor';

  if (userdata != null && userdata.isAdmin) {
    searchBarHint = searchBarHint + ', External Adjuster';
  }

  const filters: IFilterConfig[] = [
    new FilterConfig(
      'searchKey',
      FilterBarFilterType.SearchText,
      searchBarHint
    ),
    new FilterConfig(
      'clients',
      FilterBarFilterType.AsyncDropdown,
      'Client',
      {
        readEndpoint: ENDPOINTS.clients.dropdownItems,
      },
      (value) => value.name,
      true
    ),
    new FilterConfig(
      'benefitStates',
      FilterBarFilterType.EnumDropdown,
      'State of Jurisdiction',
      { data: state },
      (value) => value.displayName,
      true
    ),
    new FilterConfig(
      'appointmentDate',
      FilterBarFilterType.DatePicker,
      'Appointment Date'
    ),
    new FilterConfig(
      'referralDateFrom',
      FilterBarFilterType.DatePicker,
      'Referral Date From'
    ),
    new FilterConfig(
      'referralDateTo',
      FilterBarFilterType.DatePicker,
      'Referral Date To'
    ),
    new FilterConfig(
      'reportDueDateFromReviewer',
      FilterBarFilterType.DatePicker,
      'Due Date From Reviewer'
    ),
    new FilterConfig(
      'reportDueDateToClient',
      FilterBarFilterType.DatePicker,
      'Referral Due Date'
    ),
    new FilterConfig(
      'reviewers',
      FilterBarFilterType.AsyncDropdown,
      'Reviewer',
      {
        readEndpoint: `${ENDPOINTS.users.endpointString(
          'DropdownItems'
        )}?IsReviewer=true`,
        useQuery: true,
      },
      (value) => value.name,
      true
    ),
    new FilterConfig(
      'reopenedDateFrom',
      FilterBarFilterType.DatePicker,
      'Re-Opened Date From'
    ),
    new FilterConfig(
      'reopenedDateTo',
      FilterBarFilterType.DatePicker,
      'Re-Opened Date To'
    ),
    new StaticFilterConfig('clientId'),
  ];

  const [gridToggler] = useGridSearch();
  const [filtersEnabled, setFiltersEnabled] = useState(true);
  const dateColumnWidth = filtersEnabled ? '260px' : '200px';

  let externalAdjusterColum;

  if (userdata != null && userdata.isAdmin) {
    externalAdjusterColum = (
      <Column field="externalAdjuster" title="External Adjuster" />
    );
  }

  function navigateToReferral(
    id: string,
    status: string,
    lob: string,
    clientId: string
  ) {
    if (status === Status.SUBMITTED || status === Status.PENDING) {
      history.push(
        buildRoute(
          ROUTES.ADD_EDIT,
          AddEditSubRoute.ReferralsRequest,
          id + '?lob=' + lob + '&clientId=' + clientId
        )
      );
    } else if (status == Status.CONVERTED) {
      history.push(
        buildRoute(AddEditSubRoute.ReferralsRequest, id, ROUTES.VIEW)
      );
    } else {
      history.push(buildRoute(AddEditSubRoute.Referral, id, ROUTES.VIEW));
    }
  }
  function onRowClick(dataItem: any) {
    navigateToReferral(
      dataItem?.id,
      dataItem?.status,
      dataItem?.lineOfBusiness?.value,
      dataItem?.clientId
    );
  }

  return (
    <FilterBarMainGrid
      gridId="OpenReferrals"
      isMainGrid
      headerTitle="Referrals"
      onRowClick={(e) => onRowClick(e.dataItem)}
      filterBarProps={{
        filterConfigurations: filters,
        filterValues: {},
        isColumnSelectorEnabled: false,
      }}
      refreshToggle={gridToggler}
      readEndpoint={ENDPOINTS.opencases.read}
      apiVersion="3.0"
      exportEndpoint={`${ENDPOINTS.opencases.base}/exportToCSV`}
      exportEndpointVersion="3.0"
      enableGridRefresh={true}
      defaultFiltersEnabled={true}
      onFiltersEnabledChange={setFiltersEnabled}
      {...defaultGridProps}
      style={{ cursor: 'pointer' }}
    >
      <Column
        field="stradixNumber"
        cell={requestOrReferralCell}
        title="Stradix Number"
        width="180px"
        locked={true}
        filterable
        filterCell={DebouncedTextFilterCell}
        columnMenu={ColumnMenu}
      />
      <Column
        field="claimantName"
        title="Claimant Name"
        filterable
        filterCell={DebouncedTextFilterCell}
        width="180px"
        columnMenu={ColumnMenu}
      />
      {/* The conditionals for Client Name and Re-Opened Date are the same, but we can't use <></> because it will break the designs */}
      {(userdata?.isInternal ||
        userdata?.isDoctorAdmin ||
        userdata?.isDoctor ||
        userdata?.isReviewer) && (
          <Column
            field="client"
            title="Client Name"
            filterCell={DebouncedTextFilterCell}
            width="180px"
            columnMenu={ColumnMenu}
          />
        )}
      {(userdata?.isInternal ||
        userdata?.isDoctorAdmin ||
        userdata?.isDoctor ||
        userdata?.isReviewer) && (
          <Column
            field="reOpenedDate"
            width={dateColumnWidth}
            filterable
            filterCell={(e) => <DateRangePickerFilterCell {...e} />}
            title="Re-Opened Date"
            cell={dateAndTimeFormatCell}
            columnMenu={ColumnMenu}
          />
        )}
      <Column
        field={userdata?.isReviewer ? 'reportDueDate' : 'dueDate'}
        cell={dueDateTimeCell}
        title="Due Date & Time"
        width={dateColumnWidth}
        filterable
        filterCell={(e) => <DateRangePickerFilterCell {...e} />}
        columnMenu={ColumnMenu}
      />
      <Column
        field="claimNumber"
        title="Claim/Client Number"
        filterable
        filterCell={DebouncedTextFilterCell}
        width="188px"
        columnMenu={ColumnMenu}
      />
      <Column
        field="referralDate"
        cell={dateAndTimeFormatCell}
        title="Referral Date & Time"
        filterable
        width={dateColumnWidth}
        filterCell={(e) => <DateRangePickerFilterCell {...e} />}
        columnMenu={ColumnMenu}
      />
      <Column
        field="appointmentDate"
        cell={dateAndTimeFormatCell}
        title="Appointment Date & Time"
        filterable
        width={dateColumnWidth}
        filterCell={(e) => <DateRangePickerFilterCell {...e} />}
        columnMenu={ColumnMenu}
      />
      <Column
        field="service"
        title="Service"
        filterable
        width="140px"
        filterCell={(x) =>
          DropdownFilterCell(
            x,
            undefined,
            filtersStatus === 'success' ? filtersModel.services : []
          )
        }
        columnMenu={ColumnMenu}
      />
      <Column
        field="subService"
        title="Sub Service"
        filterCell={(x) =>
          DropdownFilterCell(
            x,
            undefined,
            filtersStatus === 'success' ? filtersModel.subServices : []
          )
        }
        width="140px"
        columnMenu={ColumnMenu}
      />
      <Column
        field="benefitState"
        title="State of Jurisdiction"
        width="140px"
        filterCell={(x) => DropdownFilterCell(x, state)}
        columnMenu={ColumnMenu}
      />
      {!isClient && (
        <Column
          field="status"
          cell={(e) =>
            referralStatusByType({
              ...e,
              onClick: () => onRowClick(e.dataItem),
            })
          }
          title="Status"
          width="180px"
          filterCell={(x) => DropdownFilterCell(x, referralStatus)}
          columnMenu={ColumnMenu}
        />
      )}
      {isClient && (
        <Column
          field="caseStatus"
          cell={(e) =>
            caseStatusByType({
              ...e,
              onClick: () => onRowClick(e.dataItem),
            })
          }
          title="Case Status"
          width="180px"
          filterCell={(x) => DropdownFilterCell(x, caseStatus)}
          columnMenu={ColumnMenu}
        />
      )}
      {isClient && (
        <Column
          field="reopenedDate"
          cell={dateAndTimeFormatCell}
          title="Re-Opened Date"
          filterable
          width={dateColumnWidth}
          filterCell={(e) => <DateRangePickerFilterCell {...e} />}
          columnMenu={ColumnMenu}
        />
      )}
      <Column
        field="referralSource"
        title="Submitted By"
        width="180px"
        filterCell={(x) =>
          DropdownFilterCell(
            x,
            undefined,
            filtersStatus === 'success' ? filtersModel.submittedBy : []
          )
        }
        columnMenu={ColumnMenu}
      />
      <Column
        field="lineOfBusinessName"
        title="Line Of Business"
        cell={lineOfBusinessCell}
        width="180px"
        filterable
        filterCell={(x) => DropdownFilterCell(x, lineOfBusiness)}
        columnMenu={ColumnMenu}
      />
      {!isClient && (
        <Column
          field="ownerName"
          title="Owner"
          width="160px"
          columnMenu={ColumnMenu}
        />
      )}
      <Column width={0}></Column>
    </FilterBarMainGrid>
  );
};

const linkDateColumn: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <DateLinkCell
      endpoint={ROUTES.REFERRAL}
      linkField="referralId"
      subRoute={ReferralSubRoute.View}
      customClassName={
        dayjs
          .utc(props.dataItem.appointmentDate)
          .local()
          .startOf('day')
          .isSame(dayjs().startOf('day'), 'day')
          ? 'due-today-cell'
          : ''
      }
      dateFormat={DEFAULT_DATETIME24H_FORMAT}
      {...props}
    />
  );
};

const lineOfBusinessCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <td>{props.dataItem.lineOfBusiness?.displayName}</td>;
};

const reporDueDateCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <DateLinkCell
      endpoint={ROUTES.REFERRAL}
      linkField="referralId"
      subRoute={ReferralSubRoute.View}
      customClassName={
        dayjs
          .utc(props.dataItem.reportDueDate)
          .local()
          .startOf('day')
          .isSame(dayjs().startOf('day'), 'day')
          ? 'due-today-cell'
          : ''
      }
      dateFormat={DEFAULT_DATETIME24H_FORMAT}
      {...props}
    />
  );
};

const claimNumberCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <LinkCell
      endpoint={ROUTES.CLAIM}
      linkField="claimId"
      subRoute={ClaimsSubRoute.View}
      {...props}
    ></LinkCell>
  );
};

const claimantCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <LinkCell
      endpoint={ROUTES.CLAIMANT}
      linkField="claimantId"
      subRoute={ClaimantsSubRoute.View}
      {...props}
    ></LinkCell>
  );
};

const referralCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <PureCell
      style={{ borderRight: '1px solid #e0e0e0' }}
      linkField="referralId"
      subRoute={ClaimsSubRoute.View}
      {...props}
      bold
    />
  );
};

const requestOrReferralCell: React.FC<GridCellProps> = (
  props: GridCellProps
) => {
  function referralRoute(
    id: string,
    status: string,
    lob: string,
    clientId: string
  ) {
    if (status === Status.SUBMITTED || status === Status.PENDING) {
      return buildRoute(
        ROUTES.ADD_EDIT,
        AddEditSubRoute.ReferralsRequest,
        id + '?lob=' + lob + '&clientId=' + clientId
      );
    } else {
      return buildRoute(AddEditSubRoute.Referral, id, ROUTES.VIEW);
    }
  }
  return (
    <>
      {
        <LinkCell
          className="k-grid-content-sticky"
          to={referralRoute(
            props.dataItem.id,
            props.dataItem.status,
            props.dataItem.lineOfBusiness?.value,
            props.dataItem.clientId
          )}
          linkField="id"
          {...props}
          style={{ borderRight: '1px solid #e0e0e0', left: 0, right: 0 }}
          bold
        ></LinkCell>
      }
    </>
  );
};

const dateFormatCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <DateCell {...props} dateFormat={'MM/DD/YYYY'} />;
};

const dateAndTimeFormatCell: React.FC<GridCellProps> = (
  props: GridCellProps
) => {
  return <DateCell {...props} dateFormat={DEFAULT_DATETIME24H_FORMAT} />;
};

interface ReferralStatusByTypeProps extends GridCellProps {
  onClick?: () => void;
}

const referralStatusByType: React.FC<ReferralStatusByTypeProps> = (
  props: ReferralStatusByTypeProps
) => {
  return (
    <td onClick={props.onClick}>
      <StatusPill
        status={
          props.dataItem.openReferralType === 'Submitted'
            ? props.dataItem.openReferralType
            : props.dataItem.status
        }
      />
    </td>
  );
};

const caseStatusByType: React.FC<ReferralStatusByTypeProps> = (
  props: ReferralStatusByTypeProps
) => {
  return (
    <td onClick={props.onClick}>
      <StatusPill status={props.dataItem.status} />
    </td>
  );
};

const getSelectedColumnsQuery = (columnSelectorState: any) => {
  const selectedColumns = columnSelectorState.columns
    .filter((x: any) => x.checked)
    .map((x: any) => x.field);
  return '?' + selectedColumns.map((x: any) => `columns=${x}`).join('&');
};
